"use client";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import type { SetupFilter } from "@/lib/setup-checklist-sections";
import { cx, tv } from "@/lib/tailwind-utils";

const setupFilterBarVariants = tv({
  slots: {
    group: "bg-background-secondary",
    option:
      "tabular-nums text-foreground-muted data-[state=active]:shadow-sm motion-reduce:transition-none",
  },
  variants: {
    size: {
      sm: {
        group: "rounded-md p-0.5",
        option: "rounded px-2 py-0.5 text-tiny font-medium",
      },
      md: {
        group: "rounded-lg p-1",
        option: "rounded-md px-3 py-1 text-body-strong",
      },
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

const ALL = "all";

export function SetupFilterBar({
  value,
  onChange,
  requiredOpen,
  criticalOpen,
  size = "sm",
}: {
  value: SetupFilter | null;
  onChange: (next: SetupFilter | null) => void;
  requiredOpen: number;
  criticalOpen: number;
  size?: "sm" | "md";
}) {
  const styles = setupFilterBarVariants({ size });
  const options: { id: SetupFilter | null; label: string; count?: number }[] = [
    { id: null, label: "All" },
    { id: "critical", label: "Critical", count: criticalOpen },
    { id: "required", label: "Required", count: requiredOpen },
  ];
  return (
    <Tabs
      value={value ?? ALL}
      onValueChange={(next) =>
        onChange(next === ALL ? null : (next as SetupFilter))
      }
    >
      <TabsList variant="solid" aria-label="Show" className={styles.group()}>
        {options.map((option) => {
          const critical = option.id === "critical";
          return (
            <TabsTrigger
              key={option.label}
              value={option.id ?? ALL}
              className={styles.option({
                className: critical
                  ? "data-[state=active]:text-destructive"
                  : "data-[state=active]:text-foreground",
              })}
            >
              {option.label}
              {option.count !== undefined && option.count > 0 && (
                <span
                  className={cx(
                    "ml-1",
                    critical ? "text-destructive" : "text-foreground-muted",
                  )}
                >
                  {option.count}
                </span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
